/**
 * R12 narrative-layer validation — voice lines, logs' siblings, and the
 * occurrence matchers, held to the same laws as everything else authored.
 *
 * @remarks
 * Four rule families, all step-3 (no lexicon needed — even the
 * `command_executed.intent` check validates against the ENGINE'S action
 * vocabulary, which is a compile-time export, not a pack compile product):
 *
 * 1. **Dead voice lines** — a declared line nothing ever speaks is the
 *    D31/D47 law, voice-side.
 * 2. **Dead pool tails** — `once: true` retires a line after ONE speaking,
 *    so every pool entry after the first could never play.
 * 3. **Matcher discriminants** — an authored `failureKind` / `reason` /
 *    `intent` is checked against the parser's and engine's own exported
 *    lists; `reactionKey` against the pack's declared reactions. A typo here
 *    would otherwise be a watcher listening for an occurrence that cannot
 *    occur — dead content with extra steps.
 * 4. **Voice-only effects on non-executed occurrences** — a failed parse, a
 *    rejected command, or a spoken reaction consumed no turn, so a trigger
 *    listening for one may only SPEAK (`append_narrative` /
 *    `speak_voice_line`): D27's "voice, not mechanics," generalized and
 *    machine-enforced. D246: a branch among them may choose WHAT to say —
 *    `choose` moves nothing itself — and every arm is held to the same law.
 *
 * @packageDocumentation
 */

import { type ContentPackDefinition, effectHomesWithin, type OccurrenceMatcher } from '@orpheus/content-schema';
import { ACTION_REJECTION_REASON_TYPES, RUNTIME_ACTION_TYPES } from '@orpheus/engine-runtime';
import { PARSE_FAILURE_KINDS } from '@orpheus/parser-engine';

import { everyEffectOf } from './effect-sources.js';
import { type ReferenceBrokenFailure } from './load-failure.js';
import { brokenReference } from './semantic-validation.js';

/** The effects a non-executed occurrence trigger may carry — voice, not mechanics; and (D246) the branch that chooses which. */
const VOICE_ONLY_EFFECT_TYPES: ReadonlySet<string> = new Set(['append_narrative', 'choose', 'speak_voice_line']);

/** The matchers that listen at an EXECUTED turn — a command's, or (D235) an answer's — whose triggers may move the world. */
const EXECUTED_TURN_MATCHER_TYPES: ReadonlySet<OccurrenceMatcher['type']> = new Set(['command_executed', 'dialogue_answered']);

/**
 * D235: a `dialogue_answered` matcher names a prompt some actor declares, and
 * an option that prompt offers (split out at the complexity cap).
 *
 * @param matcher - The matcher
 * @param matcherPath - Its path, for the failures
 * @param contentPack - The parsed pack
 * @returns Every failure found
 */
const checkDialogueMatcher = (
	matcher: Extract<OccurrenceMatcher, { type: 'dialogue_answered' }>,
	matcherPath: string,
	contentPack: ContentPackDefinition,
): readonly ReferenceBrokenFailure[] => {
	const prompt = contentPack.actors.flatMap((actor) => actor.prompts ?? []).find((candidate) => candidate.promptId === matcher.promptId);

	if (matcher.promptId !== undefined && prompt === undefined) {
		return [brokenReference(`${matcherPath}.promptId`, matcher.promptId, 'no actor declares a prompt by that id')];
	}

	if (prompt !== undefined && matcher.optionId !== undefined && !prompt.options.some((option) => option.optionId === matcher.optionId)) {
		return [brokenReference(`${matcherPath}.optionId`, matcher.optionId, `prompt "${prompt.promptId}" offers no option by that id`)];
	}

	return [];
};

/**
 * Run every R12 narrative-layer rule, collecting all failures.
 *
 * @param contentPack - The parsed, schema-valid pack
 * @returns Every narrative-authoring failure found (empty = clean)
 */
export const validateNarrativeAuthoring = (contentPack: ContentPackDefinition): readonly ReferenceBrokenFailure[] => {
	const failures: ReferenceBrokenFailure[] = [];

	// 1 + 2 — the voice-line laws.
	const spokenVoiceLineIds = new Set(
		everyEffectOf(contentPack)
			.filter((effect) => effect.type === 'speak_voice_line')
			.map((effect) => effect.voiceLineId as string),
	);

	contentPack.voiceLines.forEach((voiceLine, voiceLineIndex) => {
		if (!spokenVoiceLineIds.has(voiceLine.voiceLineId)) {
			failures.push(
				brokenReference(
					`voiceLines[${String(voiceLineIndex)}].voiceLineId`,
					voiceLine.voiceLineId,
					'no speak_voice_line anywhere utters this line, so it can never play',
				),
			);
		}

		if (voiceLine.once && voiceLine.pool.length > 1) {
			failures.push(
				brokenReference(
					`voiceLines[${String(voiceLineIndex)}].pool`,
					voiceLine.voiceLineId,
					'a once-only line retires after its first speaking, so every pool entry after the first could never play',
				),
			);
		}
	});

	// 3 + 4 — the matcher laws.
	contentPack.triggers.forEach((trigger, triggerIndex) => {
		const matcher = trigger.on;

		if (matcher === undefined) {
			return;
		}

		const matcherPath = `triggers[${String(triggerIndex)}].on`;

		switch (matcher.type) {
			case 'command_executed': {
				if (matcher.intent !== undefined && !(RUNTIME_ACTION_TYPES as readonly string[]).includes(matcher.intent)) {
					failures.push(
						brokenReference(
							`${matcherPath}.intent`,
							matcher.intent,
							'not an engine action type — the matcher listens for the ACTION a command executes as, not the surface verb',
						),
					);
				}
				break;
			}
			case 'command_rejected': {
				if (
					matcher.reason !== undefined &&
					!(ACTION_REJECTION_REASON_TYPES as readonly string[]).includes(matcher.reason)
				) {
					failures.push(brokenReference(`${matcherPath}.reason`, matcher.reason, 'not a rejection reason the engine can emit'));
				}
				break;
			}
			// D235: the prompt named is one some actor declares, and the option one of that prompt's.
			case 'dialogue_answered': {
				failures.push(...checkDialogueMatcher(matcher, matcherPath, contentPack));
				break;
			}
			case 'parse_failed': {
				if (matcher.failureKind !== undefined && !(PARSE_FAILURE_KINDS as readonly string[]).includes(matcher.failureKind)) {
					failures.push(
						brokenReference(`${matcherPath}.failureKind`, matcher.failureKind, 'not a parse-failure kind the parser can emit'),
					);
				}
				break;
			}
			case 'reaction_spoken': {
				if (matcher.reactionKey !== undefined && contentPack.reactions?.[matcher.reactionKey] === undefined) {
					failures.push(
						brokenReference(`${matcherPath}.reactionKey`, matcher.reactionKey, 'the pack declares no reaction under this key'),
					);
				}
				break;
			}
		}

		// A non-executed occurrence moved no time and may move no world. D235:
		// an answer to an actor's question is an executed turn, and may.
		if (!EXECUTED_TURN_MATCHER_TYPES.has(matcher.type)) {
			for (const [effectsPath, effects] of effectHomesWithin([`triggers[${String(triggerIndex)}].effects`, trigger.effects])) {
				effects.forEach((effect, effectIndex) => {
					if (!VOICE_ONLY_EFFECT_TYPES.has(effect.type)) {
						failures.push(
							brokenReference(
								`${effectsPath}[${String(effectIndex)}]`,
								trigger.triggerId,
								`a trigger listening for ${matcher.type} may only speak — a refused input never moves the world`,
							),
						);
					}
				});
			}
		}
	});

	return failures;
};
